// features/inventory/pages/InventoryDashboardPage.tsx
/**
 * Inventory Dashboard — Unified entry point for all inventory operations.
 *
 * Replaces: InventoryPage.tsx + InventoryDashboardPage.tsx
 *
 * Features:
 * - Real-time stat cards (low stock, pending review, active counts, transfers)
 * - Quick action grid
 * - Low stock alert banner (top 5, expandable)
 * - Stock confirmation progress bar
 * - Recent counts & transfers activity feeds
 * - Setup wizard for first-time users (no stock data yet)
 * - Branch selector with persistent preference
 * - Offline-aware — shows sync status
 */

import { useState, useMemo, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { useOfflineStore } from '@/store/offlineStore';
import { Card, CardContent } from '@/components/ui/Card';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { getCountSessions, getTransfers } from '../services/inventoryApi';
import {
    getLowStockAlerts,
    getStockConfirmationStatus,
} from '@/features/stock/services/stockApi';
import { getBranches } from '@/features/settings/services/settingsApi';
import {
    Warehouse,
    ClipboardList,
    ArrowRightLeft,
    Package,
    AlertTriangle,
    ShieldCheck,
    Layers,
    Settings,
    Truck,
    Zap,
    Bell,
    RefreshCw,
    ChevronRight,
    ClipboardCheck,
    RotateCcw,
    History,
    WifiOff,
    Wifi,
    ArrowRight,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { clsx } from 'clsx';

// ============================================================
// TYPES
// ============================================================

interface QuickActionConfig {
    label: string;
    description: string;
    icon: React.ElementType;
    gradient: string;
    href: string;
    disabled?: boolean;
    disabledReason?: string;
}

// ============================================================
// CONSTANTS
// ============================================================

const NIL_UUID = '00000000-0000-0000-0000-000000000000';

const QUICK_ACTIONS: QuickActionConfig[] = [
    {
        label: 'View Stock',
        description: 'Search, filter, and monitor all stock levels',
        icon: Layers,
        gradient: 'from-emerald-500 to-teal-600',
        href: '/inventory/levels',
    },
    {
        label: 'Start Count',
        description: 'Begin a new physical inventory count',
        icon: ClipboardCheck,
        gradient: 'from-blue-500 to-indigo-600',
        href: '/inventory/counts',
    },
    {
        label: 'New Transfer',
        description: 'Move stock between branches',
        icon: ArrowRightLeft,
        gradient: 'from-purple-500 to-indigo-600',
        href: '/inventory/transfers',
    },
    {
        label: 'Adjust Stock',
        description: 'Correct waste, damage, or discrepancies',
        icon: RotateCcw,
        gradient: 'from-amber-500 to-orange-600',
        href: '/inventory/adjust',
    },
    {
        label: 'Review Stock',
        description: 'Review counted stock before confirming',
        icon: ShieldCheck,
        gradient: 'from-emerald-500 to-teal-600',
        href: '/inventory/review',
    },
    {
        label: 'Transaction Ledger',
        description: 'Full audit trail of all stock movements',
        icon: History,
        gradient: 'from-slate-500 to-slate-700',
        href: '/inventory/ledger',
    },
];

// ============================================================
// SUB-COMPONENTS
// ============================================================

function QuickActionButton({ action }: { action: QuickActionConfig }) {
    const navigate = useNavigate();

    return (
        <motion.button
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            whileHover={!action.disabled ? { y: -4, scale: 1.02 } : undefined}
            whileTap={!action.disabled ? { scale: 0.98 } : undefined}
            onClick={() => !action.disabled && navigate(action.href)}
            disabled={action.disabled}
            className={clsx(
                'relative p-4 rounded-2xl border-2 text-left transition-all',
                action.disabled
                    ? 'border-slate-200 bg-slate-50 opacity-50 cursor-not-allowed'
                    : 'border-slate-200 bg-white hover:border-emerald-300 hover:shadow-lg'
            )}
            type="button"
        >
            <div className="flex flex-col items-center text-center gap-2">
                <div
                    className={clsx(
                        'p-3 rounded-xl bg-gradient-to-br shadow-lg',
                        action.gradient
                    )}
                >
                    <action.icon className="w-5 h-5 text-white" />
                </div>
                <div>
                    <h4 className="font-bold text-xs text-slate-800">
                        {action.label}
                    </h4>
                    <p className="text-[10px] text-slate-400 mt-0.5 leading-tight">
                        {action.description}
                    </p>
                </div>
            </div>
            {action.disabled && action.disabledReason && (
                <div className="absolute inset-0 bg-white/40 rounded-2xl flex items-center justify-center">
                    <p className="text-[10px] text-slate-400 font-medium text-center px-2">
                        {action.disabledReason}
                    </p>
                </div>
            )}
        </motion.button>
    );
}

function ConfirmationProgressBar({
    total,
    confirmed,
    counted,
    pending,
}: {
    total: number;
    confirmed: number;
    counted: number;
    pending: number;
}) {
    const confirmedPercent = total > 0 ? Math.round((confirmed / total) * 100) : 0;
    const countedPercent = total > 0 ? Math.round((counted / total) * 100) : 0;
    const pendingPercent = total > 0 ? Math.round((pending / total) * 100) : 0;

    if (total === 0) return null;

    return (
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
            <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 overflow-hidden">
                <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                            <div className="p-2.5 rounded-xl bg-blue-100">
                                <ShieldCheck className="w-5 h-5 text-blue-600" />
                            </div>
                            <div>
                                <h3 className="font-bold text-blue-800">
                                    Stock Status
                                </h3>
                                <p className="text-sm text-blue-600">
                                    {confirmed.toLocaleString()} of{' '}
                                    {total.toLocaleString()} confirmed ·{' '}
                                    {confirmedPercent}% sellable
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center gap-4 text-xs font-medium">
                            <span className="flex items-center gap-1.5">
                                <span className="w-2.5 h-2.5 rounded-full bg-amber-500" />
                                Pending: {pending}
                            </span>
                            <span className="flex items-center gap-1.5">
                                <span className="w-2.5 h-2.5 rounded-full bg-blue-500" />
                                Counted: {counted}
                            </span>
                            <span className="flex items-center gap-1.5">
                                <span className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
                                Confirmed: {confirmed}
                            </span>
                        </div>
                    </div>
                    <div className="h-2.5 bg-blue-100 rounded-full overflow-hidden">
                        <div className="h-full flex">
                            <motion.div
                                className="bg-emerald-500"
                                initial={{ width: 0 }}
                                animate={{ width: `${confirmedPercent}%` }}
                                transition={{ duration: 1, ease: 'easeOut' }}
                            />
                            <motion.div
                                className="bg-blue-500"
                                initial={{ width: 0 }}
                                animate={{ width: `${countedPercent}%` }}
                                transition={{
                                    duration: 1,
                                    delay: 0.2,
                                    ease: 'easeOut',
                                }}
                            />
                            <motion.div
                                className="bg-amber-500"
                                initial={{ width: 0 }}
                                animate={{ width: `${pendingPercent}%` }}
                                transition={{
                                    duration: 1,
                                    delay: 0.4,
                                    ease: 'easeOut',
                                }}
                            />
                        </div>
                    </div>
                </CardContent>
            </Card>
        </motion.div>
    );
}

function LowStockBanner({
    alerts,
    onViewAll,
}: {
    alerts: any[];
    onViewAll: () => void;
}) {
    const navigate = useNavigate();
    const [expanded, setExpanded] = useState(false);

    if (alerts.length === 0) return null;

    const displayedAlerts = expanded ? alerts : alerts.slice(0, 3);

    return (
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
            <Card className="border-2 border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50 overflow-hidden">
                <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                            <motion.div
                                animate={{ rotate: [0, 10, -10, 0] }}
                                transition={{ duration: 2, repeat: Infinity }}
                                className="p-2 rounded-xl bg-amber-100"
                            >
                                <Bell className="w-5 h-5 text-amber-600" />
                            </motion.div>
                            <div>
                                <h3 className="font-bold text-amber-800">
                                    {alerts.length} Product
                                    {alerts.length !== 1 ? 's' : ''} Need
                                    {alerts.length === 1 ? 's' : ''} Attention
                                </h3>
                                <p className="text-xs text-amber-600">
                                    Stock is below the reorder threshold
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            {alerts.length > 3 && (
                                <button
                                    onClick={() => setExpanded(!expanded)}
                                    className="text-xs font-medium text-amber-700 hover:text-amber-800 flex items-center gap-1"
                                    type="button"
                                >
                                    {expanded
                                        ? 'Show Less'
                                        : `View All (${alerts.length})`}
                                    <ChevronRight
                                        className={clsx(
                                            'w-3 h-3 transition-transform',
                                            expanded && 'rotate-90'
                                        )}
                                    />
                                </button>
                            )}
                            <button
                                onClick={onViewAll}
                                className="px-3 py-1.5 bg-amber-100 text-amber-700 rounded-lg text-xs font-semibold hover:bg-amber-200 transition-colors"
                                type="button"
                            >
                                Go to Stock Levels
                            </button>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                        <AnimatePresence>
                            {displayedAlerts.map((alert: any) => (
                                <motion.div
                                    key={`${alert.productId}-${alert.branchId}`}
                                    initial={{ opacity: 0, scale: 0.95 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    exit={{ opacity: 0, scale: 0.95 }}
                                    onClick={() =>
                                        navigate(
                                            `/inventory/levels/${alert.productId}`
                                        )
                                    }
                                    className="flex items-center justify-between p-3 bg-white rounded-xl border border-amber-200 cursor-pointer hover:shadow-md hover:border-amber-300 transition-all group"
                                >
                                    <div className="min-w-0 flex-1">
                                        <p className="text-sm font-semibold text-slate-800 truncate group-hover:text-emerald-600 transition-colors">
                                            {alert.productName || 'Unknown'}
                                        </p>
                                        <div className="flex items-center gap-2 mt-0.5">
                                            <span className="text-xs text-slate-400">
                                                {alert.branchName || 'Warehouse'}
                                            </span>
                                            <span className="text-xs font-bold text-amber-600">
                                                {parseFloat(
                                                    alert.availableQuantity || '0'
                                                ).toLocaleString()}{' '}
                                                left
                                            </span>
                                            <span className="text-[10px] text-slate-400">
                                                (threshold: {alert.lowStockThreshold})
                                            </span>
                                        </div>
                                    </div>
                                    <ArrowRight className="w-4 h-4 text-amber-400 flex-shrink-0 ml-2 opacity-0 group-hover:opacity-100 transition-opacity" />
                                </motion.div>
                            ))}
                        </AnimatePresence>
                    </div>
                </CardContent>
            </Card>
        </motion.div>
    );
}

function RecentCountsList({
    counts,
    isLoading,
}: {
    counts: any[];
    isLoading: boolean;
}) {
    const navigate = useNavigate();

    const formatDate = (dateStr: string | undefined): string => {
        if (!dateStr) return '—';
        return new Date(dateStr).toLocaleDateString('en-NG', {
            day: 'numeric',
            month: 'short',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    return (
        <Card>
            <CardContent className="p-0">
                <div className="p-5 border-b border-slate-100 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="p-2 rounded-xl bg-gradient-to-br from-blue-100 to-indigo-100">
                            <ClipboardList className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                            <h3 className="font-semibold text-slate-800">
                                Recent Counts
                            </h3>
                            <p className="text-xs text-slate-500">
                                {counts.length} sessions
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={() => navigate('/inventory/counts')}
                        className="text-xs font-medium text-blue-600 hover:text-blue-700 flex items-center gap-1"
                        type="button"
                    >
                        View All
                        <ChevronRight className="w-3.5 h-3.5" />
                    </button>
                </div>
                <div className="divide-y divide-slate-50">
                    {isLoading ? (
                        <div className="flex justify-center py-8">
                            <Spinner size="md" />
                        </div>
                    ) : counts.length === 0 ? (
                        <EmptyState
                            icon={<ClipboardCheck className="w-10 h-10" />}
                            title="No count sessions yet"
                            description="Start your first inventory count to reconcile physical stock with system records."
                            action={{
                                label: 'Start First Count',
                                onClick: () => navigate('/inventory/counts'),
                            }}
                            size="sm"
                        />
                    ) : (
                        counts.slice(0, 5).map((count: any) => {
                            const progress =
                                count.totalItems > 0
                                    ? Math.round(
                                        (count.countedItems / count.totalItems) * 100
                                    )
                                    : 0;
                            return (
                                <div
                                    key={count.id}
                                    onClick={() =>
                                        navigate(`/inventory/counts/${count.id}`)
                                    }
                                    className="p-4 hover:bg-slate-50 cursor-pointer transition-colors flex items-center justify-between"
                                >
                                    <div className="flex items-center gap-3">
                                        <div
                                            className={clsx(
                                                'w-2 h-2 rounded-full',
                                                count.status === 'completed'
                                                    ? 'bg-emerald-500'
                                                    : count.status === 'in_progress'
                                                        ? 'bg-blue-500 animate-pulse'
                                                        : count.status === 'cancelled'
                                                            ? 'bg-rose-500'
                                                            : 'bg-slate-400'
                                            )}
                                        />
                                        <div>
                                            <p className="text-sm font-medium text-slate-700">
                                                {count.countType?.toUpperCase()} Count
                                            </p>
                                            <p className="text-xs text-slate-400">
                                                {count.branchName || 'Warehouse'} ·{' '}
                                                {formatDate(count.createdAt)}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <p className="text-sm font-medium text-slate-700">
                                            {count.countedItems}/{count.totalItems}
                                        </p>
                                        <div className="w-20 h-1.5 bg-slate-200 rounded-full mt-1">
                                            <div
                                                className={clsx(
                                                    'h-1.5 rounded-full',
                                                    count.status === 'completed'
                                                        ? 'bg-emerald-500'
                                                        : 'bg-blue-500'
                                                )}
                                                style={{ width: `${progress}%` }}
                                            />
                                        </div>
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>
            </CardContent>
        </Card>
    );
}

function RecentTransfersList({
    transfers,
    isLoading,
    isSingleStore,
}: {
    transfers: any[];
    isLoading: boolean;
    isSingleStore: boolean;
}) {
    const navigate = useNavigate();

    const formatDate = (dateStr: string | undefined): string => {
        if (!dateStr) return '—';
        return new Date(dateStr).toLocaleDateString('en-NG', {
            day: 'numeric',
            month: 'short',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    const statusColors: Record<string, string> = {
        pending: 'bg-amber-100 text-amber-700',
        approved: 'bg-blue-100 text-blue-700',
        in_transit: 'bg-purple-100 text-purple-700',
        received: 'bg-emerald-100 text-emerald-700',
        cancelled: 'bg-rose-100 text-rose-700',
    };

    return (
        <Card>
            <CardContent className="p-0">
                <div className="p-5 border-b border-slate-100 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="p-2 rounded-xl bg-gradient-to-br from-purple-100 to-indigo-100">
                            <ArrowRightLeft className="w-5 h-5 text-purple-600" />
                        </div>
                        <div>
                            <h3 className="font-semibold text-slate-800">
                                Recent Transfers
                            </h3>
                            <p className="text-xs text-slate-500">
                                {transfers.length} transfers
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={() => navigate('/inventory/transfers')}
                        className="text-xs font-medium text-purple-600 hover:text-purple-700 flex items-center gap-1"
                        type="button"
                    >
                        View All
                        <ChevronRight className="w-3.5 h-3.5" />
                    </button>
                </div>
                <div className="divide-y divide-slate-50">
                    {isLoading ? (
                        <div className="flex justify-center py-8">
                            <Spinner size="md" />
                        </div>
                    ) : isSingleStore ? (
                        <EmptyState
                            icon={<ArrowRightLeft className="w-10 h-10" />}
                            title="Transfers Unavailable"
                            description="Stock transfers require at least 2 branches. Configure additional branches in Settings."
                            action={{
                                label: 'Configure Branches',
                                onClick: () => navigate('/settings/branches'),
                            }}
                            size="sm"
                        />
                    ) : transfers.length === 0 ? (
                        <EmptyState
                            icon={<ArrowRightLeft className="w-10 h-10" />}
                            title="No transfers yet"
                            description="Create your first stock transfer to move inventory between branches."
                            action={{
                                label: 'New Transfer',
                                onClick: () => navigate('/inventory/transfers'),
                            }}
                            size="sm"
                        />
                    ) : (
                        transfers.slice(0, 5).map((transfer: any) => (
                            <div
                                key={transfer.id}
                                onClick={() => navigate('/inventory/transfers')}
                                className="p-4 hover:bg-slate-50 cursor-pointer transition-colors"
                            >
                                <div className="flex items-center justify-between mb-1">
                                    <div className="flex items-center gap-2 text-sm">
                                        <span className="font-medium">
                                            {transfer.fromBranchName || 'Source'}
                                        </span>
                                        <ArrowRightLeft className="w-3.5 h-3.5 text-slate-400" />
                                        <span className="font-medium">
                                            {transfer.toBranchName || 'Dest'}
                                        </span>
                                    </div>
                                    <span
                                        className={clsx(
                                            'px-2 py-0.5 rounded-full text-[10px] font-medium',
                                            statusColors[transfer.status] ||
                                            'bg-slate-100 text-slate-700'
                                        )}
                                    >
                                        {transfer.status?.replace(/_/g, ' ')}
                                    </span>
                                </div>
                                <div className="flex items-center gap-3 text-xs text-slate-400">
                                    <span>{transfer.totalItems} items</span>
                                    <span>·</span>
                                    <span>
                                        {transfer.totalQuantity?.toLocaleString()} qty
                                    </span>
                                    <span>·</span>
                                    <span>{formatDate(transfer.createdAt)}</span>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </CardContent>
        </Card>
    );
}

function SetupBanner() {
    const navigate = useNavigate();

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
        >
            <Card className="border-2 border-dashed border-emerald-300 bg-gradient-to-r from-emerald-50 to-teal-50">
                <CardContent className="p-8 text-center">
                    <motion.div
                        animate={{ y: [0, -10, 0] }}
                        transition={{ duration: 3, repeat: Infinity }}
                        className="w-20 h-20 rounded-3xl bg-emerald-100 flex items-center justify-center mx-auto mb-6"
                    >
                        <Package className="w-10 h-10 text-emerald-600" />
                    </motion.div>
                    <h2 className="text-2xl font-bold text-slate-900 mb-2">
                        Welcome to Inventory Management
                    </h2>
                    <p className="text-sm text-slate-500 max-w-lg mx-auto mb-8">
                        Get started by setting up your initial stock quantities.
                        We'll generate a spreadsheet with all your products —
                        just enter the quantities and upload.
                    </p>
                    <div className="flex items-center justify-center gap-3">
                        <button
                            onClick={() => navigate('/inventory/setup')}
                            className="flex items-center gap-2 px-6 py-3.5 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-2xl hover:from-emerald-700 transition-all font-bold shadow-lg shadow-emerald-500/25"
                            type="button"
                        >
                            <Zap className="w-5 h-5" />
                            Quick Setup — Upload Stock
                        </button>
                        <button
                            onClick={() => navigate('/inventory/counts')}
                            className="flex items-center gap-2 px-6 py-3.5 bg-white border-2 border-emerald-200 text-emerald-700 rounded-2xl hover:bg-emerald-50 transition-all font-bold"
                            type="button"
                        >
                            <ClipboardCheck className="w-5 h-5" />
                            Start Counting
                        </button>
                    </div>
                </CardContent>
            </Card>
        </motion.div>
    );
}

// ============================================================
// MAIN COMPONENT
// ============================================================

export function InventoryDashboardPage() {
    const navigate = useNavigate();
    const user = useAuthStore((s) => s.user);
    const selectedBranchId = useAuthStore((s) => s.selectedBranchId);

    // Online/offline detection using navigator.onLine with event listeners
    const [isOnline, setIsOnline] = useState<boolean>(
        typeof navigator !== 'undefined' ? navigator.onLine : true
    );

    const offlineQueueCount = useOfflineStore((s) => s.queue.length);

    // Local state
    const [activeBranchId, setActiveBranchId] = useState<string>(
        selectedBranchId || NIL_UUID
    );
    const [isRefreshing, setIsRefreshing] = useState(false);

    // Listen for online/offline events
    useEffect(() => {
        const handleOnline = () => setIsOnline(true);
        const handleOffline = () => setIsOnline(false);

        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);

        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
        };
    }, []);

    // Sync branch from auth store
    useEffect(() => {
        if (selectedBranchId) {
            setActiveBranchId(selectedBranchId);
        }
    }, [selectedBranchId]);

    // ============================================================
    // QUERIES
    // ============================================================

    const { data: branches } = useQuery({
        queryKey: ['branches-dashboard', user?.tenantId],
        queryFn: async () => {
            try {
                const data = await getBranches(user!.tenantId);
                return Array.isArray(data) ? data : [];
            } catch {
                return [];
            }
        },
        enabled: !!user?.tenantId,
        staleTime: 5 * 60 * 1000,
    });

    const { data: lowStockAlerts } = useQuery({
        queryKey: ['low-stock-dash', user?.tenantId, activeBranchId],
        queryFn: () =>
            getLowStockAlerts(user!.tenantId, activeBranchId || undefined),
        enabled: !!user?.tenantId,
        refetchInterval: 60_000,
    });

    const { data: confirmStatus } = useQuery({
        queryKey: ['confirm-status-dash', user?.tenantId, activeBranchId],
        queryFn: () =>
            getStockConfirmationStatus(user!.tenantId, activeBranchId),
        enabled: !!user?.tenantId,
        refetchInterval: 30_000,
    });

    const { data: counts, isLoading: countsLoading } = useQuery({
        queryKey: ['counts-dash', user?.tenantId],
        queryFn: () => getCountSessions(user!.tenantId, { limit: 10 }),
        enabled: !!user?.tenantId,
        refetchInterval: 30_000,
    });

    const { data: transfers, isLoading: transfersLoading } = useQuery({
        queryKey: ['transfers-dash', user?.tenantId],
        queryFn: () => getTransfers(user!.tenantId, { limit: 10 }),
        enabled: !!user?.tenantId,
        refetchInterval: 30_000,
    });

    // ============================================================
    // DERIVED DATA
    // ============================================================

    const branchesList = (branches || []) as Array<{
        id: string;
        name: string;
        code?: string;
    }>;
    const isSingleStore = branchesList.length <= 1;
    const countsList = (counts || []);
    const transfersList = (transfers || []);
    const alerts = Array.isArray(lowStockAlerts) ? lowStockAlerts : [];

    const activeCounts = countsList.filter(
        (c: any) => c.status === 'draft' || c.status === 'in_progress'
    ).length;
    const pendingTransfers = transfersList.filter(
        (t: any) => t.status === 'pending'
    ).length;
    const inTransitTransfers = transfersList.filter(
        (t: any) => t.status === 'approved' || t.status === 'in_transit'
    ).length;
    const pendingReviewCount = confirmStatus?.counted || 0;
    const confirmedCount = confirmStatus?.confirmed || 0;
    const totalProducts = confirmStatus?.total || 0;
    const pendingConfirmationCount = confirmStatus?.pending || 0;

    const isFreshSetup =
        totalProducts === 0 &&
        countsList.length === 0 &&
        transfersList.length === 0;

    // Configure quick actions based on store count
    const visibleActions = useMemo(() => {
        return QUICK_ACTIONS.map((action) => {
            if (action.label === 'New Transfer' && isSingleStore) {
                return {
                    ...action,
                    disabled: true,
                    disabledReason: 'Requires multiple branches',
                };
            }
            return action;
        });
    }, [isSingleStore]);

    // ============================================================
    // HANDLERS
    // ============================================================

    const handleRefresh = useCallback(async () => {
        setIsRefreshing(true);
        await new Promise((r) => setTimeout(r, 600));
        setIsRefreshing(false);
    }, []);

    const getBranchName = useCallback(
        (id: string) => {
            if (id === NIL_UUID || !id) return 'Warehouse';
            return (
                branchesList.find((b: any) => b.id === id)?.name || 'Warehouse'
            );
        },
        [branchesList]
    );

    // ============================================================
    // RENDER
    // ============================================================

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50/30">
            {/* Header */}
            <div className="bg-white/90 backdrop-blur-md border-b border-slate-200 sticky top-0 z-30 shadow-sm">
                <div className="max-w-[1600px] mx-auto px-6 lg:px-8 py-4">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                        {/* Title */}
                        <div className="flex items-center gap-4">
                            <motion.div
                                initial={{ rotate: -10, scale: 0.9 }}
                                animate={{ rotate: 0, scale: 1 }}
                                className="p-2.5 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl shadow-lg shadow-emerald-500/25"
                            >
                                <Warehouse className="w-6 h-6 text-white" />
                            </motion.div>
                            <div>
                                <h1 className="text-2xl font-bold text-slate-900">
                                    Inventory
                                </h1>
                                <p className="text-sm text-slate-500">
                                    {isSingleStore
                                        ? 'Warehouse'
                                        : getBranchName(activeBranchId)}
                                    {totalProducts > 0 && (
                                        <span className="ml-2 text-emerald-600 font-medium">
                                            · {totalProducts.toLocaleString()}{' '}
                                            products
                                        </span>
                                    )}
                                </p>
                            </div>
                        </div>

                        {/* Actions */}
                        <div className="flex items-center gap-2">
                            {/* Offline indicator */}
                            {!isOnline && (
                                <div className="flex items-center gap-1.5 px-3 py-2 bg-amber-50 border border-amber-200 rounded-xl">
                                    <WifiOff className="w-4 h-4 text-amber-600" />
                                    <span className="text-xs font-medium text-amber-700">
                                        Offline
                                        {offlineQueueCount > 0
                                            ? ` (${offlineQueueCount})`
                                            : ''}
                                    </span>
                                </div>
                            )}

                            {/* Online indicator (shown for a moment when coming back online) */}
                            {isOnline && (
                                <div className="flex items-center gap-1.5 px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-xl">
                                    <Wifi className="w-4 h-4 text-emerald-600" />
                                    <span className="text-xs font-medium text-emerald-700">
                                        Online
                                    </span>
                                </div>
                            )}

                            {/* Branch selector */}
                            {!isSingleStore && branchesList.length > 0 && (
                                <select
                                    value={activeBranchId}
                                    onChange={(e) =>
                                        setActiveBranchId(e.target.value)
                                    }
                                    className="px-3 py-2 bg-white border border-slate-200 rounded-xl text-sm font-medium text-slate-700 focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-400"
                                >
                                    {branchesList.map((b: any) => (
                                        <option key={b.id} value={b.id}>
                                            {b.name}
                                        </option>
                                    ))}
                                </select>
                            )}

                            {/* Refresh */}
                            <button
                                onClick={handleRefresh}
                                disabled={isRefreshing}
                                className="flex items-center gap-2 px-4 py-2.5 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 transition-all text-sm font-medium text-slate-700 shadow-sm"
                                type="button"
                            >
                                <RefreshCw
                                    className={clsx(
                                        'w-4 h-4',
                                        isRefreshing && 'animate-spin'
                                    )}
                                />
                                Refresh
                            </button>

                            {/* Settings */}
                            <button
                                onClick={() =>
                                    navigate('/inventory/settings')
                                }
                                className="flex items-center gap-2 px-4 py-2.5 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 transition-all text-sm font-medium text-slate-700 shadow-sm"
                                type="button"
                            >
                                <Settings className="w-4 h-4" />
                                Settings
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className="max-w-[1600px] mx-auto p-6 lg:p-8 space-y-6">
                {/* Confirmation Progress Bar */}
                <ConfirmationProgressBar
                    total={totalProducts}
                    confirmed={confirmedCount}
                    counted={pendingReviewCount}
                    pending={pendingConfirmationCount}
                />

                {/* ========== DESCRIPTIVE GLOBAL STATS ========== */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {/* Low Stock */}
                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} whileHover={{ y: -3 }}
                        onClick={() => navigate('/inventory/levels?filter=low')}
                        className="bg-white rounded-2xl border shadow-sm p-4 hover:shadow-md transition-all cursor-pointer border-amber-200">
                        <div className="flex items-center justify-between mb-1">
                            <div className="p-2.5 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 shadow-md"><AlertTriangle className="w-5 h-5 text-white" /></div>
                            {alerts.length > 0 && totalProducts > 0 && <span className="text-[10px] text-amber-600 font-semibold">{Math.round((alerts.length / totalProducts) * 100)}%</span>}
                        </div>
                        <p className={clsx('text-2xl font-bold mt-1', alerts.length > 0 ? 'text-amber-600' : 'text-slate-900')}>{alerts.length.toLocaleString()}</p>
                        <p className="text-[10px] text-slate-400 mt-0.5">{alerts.length > 0 ? `${alerts.length} of ${totalProducts.toLocaleString()} products below threshold` : 'All products above threshold'}</p>
                        <p className="text-[10px] font-medium text-amber-600 mt-1">{alerts.length > 0 ? 'View low stock →' : 'Low Stock Alerts'}</p>
                    </motion.div>

                    {/* Pending Review */}
                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} whileHover={{ y: -3 }}
                        onClick={() => navigate('/inventory/review')}
                        className="bg-white rounded-2xl border shadow-sm p-4 hover:shadow-md transition-all cursor-pointer border-blue-200">
                        <div className="flex items-center justify-between mb-1">
                            <div className="p-2.5 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 shadow-md"><ShieldCheck className="w-5 h-5 text-white" /></div>
                            {pendingReviewCount > 0 && totalProducts > 0 && <span className="text-[10px] text-blue-600 font-semibold">{Math.round((pendingReviewCount / totalProducts) * 100)}%</span>}
                        </div>
                        <p className={clsx('text-2xl font-bold mt-1', pendingReviewCount > 0 ? 'text-blue-600' : 'text-slate-900')}>{pendingReviewCount.toLocaleString()}</p>
                        <p className="text-[10px] text-slate-400 mt-0.5">{pendingReviewCount > 0 ? `${pendingReviewCount} counted items awaiting confirmation` : 'No items awaiting review'}</p>
                        <p className="text-[10px] font-medium text-blue-600 mt-1">{pendingReviewCount > 0 ? 'Review & confirm →' : 'Pending Review'}</p>
                    </motion.div>

                    {/* Active Counts */}
                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} whileHover={{ y: -3 }}
                        onClick={() => navigate('/inventory/counts')}
                        className="bg-white rounded-2xl border shadow-sm p-4 hover:shadow-md transition-all cursor-pointer border-emerald-200">
                        <div className="flex items-center justify-between mb-1">
                            <div className="p-2.5 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 shadow-md"><ClipboardList className="w-5 h-5 text-white" /></div>
                            {countsList.length > 0 && <span className="text-[10px] text-emerald-600 font-semibold">{countsList.length} total sessions</span>}
                        </div>
                        <p className={clsx('text-2xl font-bold mt-1', activeCounts > 0 ? 'text-emerald-600' : 'text-slate-900')}>{activeCounts.toLocaleString()}</p>
                        <p className="text-[10px] text-slate-400 mt-0.5">{activeCounts > 0 ? `${activeCounts} count${activeCounts !== 1 ? 's' : ''} in progress` : 'No active counting sessions'}</p>
                        <p className="text-[10px] font-medium text-emerald-600 mt-1">{activeCounts > 0 ? 'Continue counting →' : 'Active Counts'}</p>
                    </motion.div>

                    {/* Pending Transfers */}
                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} whileHover={{ y: -3 }}
                        onClick={() => navigate('/inventory/transfers')}
                        className="bg-white rounded-2xl border shadow-sm p-4 hover:shadow-md transition-all cursor-pointer border-purple-200">
                        <div className="flex items-center justify-between mb-1">
                            <div className="p-2.5 rounded-xl bg-gradient-to-br from-purple-500 to-indigo-600 shadow-md"><Truck className="w-5 h-5 text-white" /></div>
                            {transfersList.length > 0 && <span className="text-[10px] text-purple-600 font-semibold">{transfersList.length} total</span>}
                        </div>
                        <p className={clsx('text-2xl font-bold mt-1', (pendingTransfers + inTransitTransfers) > 0 ? 'text-purple-600' : 'text-slate-900')}>{(pendingTransfers + inTransitTransfers).toLocaleString()}</p>
                        <p className="text-[10px] text-slate-400 mt-0.5">{pendingTransfers > 0 ? `${pendingTransfers} pending · ${inTransitTransfers} in transit` : inTransitTransfers > 0 ? `${inTransitTransfers} in transit` : 'No transfers pending'}</p>
                        <p className="text-[10px] font-medium text-purple-600 mt-1">{(pendingTransfers + inTransitTransfers) > 0 ? 'Manage transfers →' : 'Pending Transfers'}</p>
                    </motion.div>
                </div>

                {/* Quick Actions */}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                    {visibleActions.map((action) => (
                        <QuickActionButton
                            key={action.label}
                            action={action}
                        />
                    ))}
                </div>

                {/* Low Stock Banner */}
                <LowStockBanner
                    alerts={alerts}
                    onViewAll={() =>
                        navigate('/inventory/levels?filter=low')
                    }
                />

                {/* Recent Activity */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <RecentCountsList
                        counts={countsList}
                        isLoading={countsLoading}
                    />
                    <RecentTransfersList
                        transfers={transfersList}
                        isLoading={transfersLoading}
                        isSingleStore={isSingleStore}
                    />
                </div>

                {/* Setup Banner */}
                {isFreshSetup && <SetupBanner />}
            </div>
        </div>
    );
}